// Spending Goat Tokens against the basket.
//
// Two things have to happen before Shopify will price the discount, and the
// order matters:
//
//   1. The tokens are moved out of the balance into a hold for this basket.
//   2. The basket is written to, which is what makes Shopify re-price it.
//
// Doing it the other way round is why this used to need two clicks: the cart
// was touched while there was still no hold to find, so the discount function
// returned nothing, and afterwards nothing changed the cart again to prompt a
// re-price. The second click worked only because the first had left a hold
// behind.
//
// The re-rendered sections come back from the same request, so the totals
// update in place instead of reloading the page.

const root = () => window.Shopify?.routes?.root ?? '/';

async function readCart() {
  const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/** Asks the theme which sections need re-rendering on this page. */
function sectionsToRender() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
  );
  return sections;
}

/**
 * Writes to the basket so Shopify re-prices it, then hands the fresh cart to
 * the theme to render.
 *
 * `_goat_hold` is only there to guarantee the write is a real change: setting
 * an attribute to the value it already has doesn't necessarily trigger a
 * re-price, and changing the number of tokens has to. It is not authoritative —
 * the discount function reads the hold metafield, never this.
 */
async function repriceCart(cartToken, tokens) {
  const sections = sectionsToRender();

  const response = await fetch(`${root()}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      attributes: { _goat_cart: cartToken, _goat_hold: String(tokens) },
      sections,
    }),
  });

  // Shopify refuses cart writes in some situations — a B2B location with no
  // catalog, for one — and ignoring that makes it look as though the discount
  // is broken rather than that the basket rejected us.
  if (!response.ok) {
    const reason = await response.json().catch(() => ({}));
    throw new Error(reason.description || reason.message || "Your basket couldn't be updated.");
  }

  const cart = await response.json();

  // Fall back to a reload if the theme's cart machinery isn't on this page, so
  // the shopper still sees correct totals.
  if (window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart });
  } else {
    window.location.reload();
  }
}

/** Moves tokens into or out of the hold for this basket. */
async function setHold(cartToken, tokens) {
  const response = await fetch('/apps/tokens/apply', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tokens, cart: cartToken }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(result.error || 'Something went wrong. Please try again.');
  }
  return result;
}

class GoatTokenSpend extends HTMLElement {
  connectedCallback() {
    this.rate = Number.parseInt(this.dataset.rate, 10) || 20;
    this.spendable = Number.parseInt(this.dataset.spendable, 10) || 0;
    this.amount = this.querySelector('[data-gt-amount]');
    this.button = this.querySelector('[data-gt-apply]');
    this.status = this.querySelector('[data-gt-status]');
    this.worth = this.querySelector('[data-gt-worth]');

    this.button?.addEventListener('click', () => this.apply());
    this.amount?.addEventListener('input', () => this.preview());
  }

  // Shows what the typed amount is worth before they commit to it. Clamps as
  // they type, so it can't promise a discount larger than their balance.
  preview() {
    if (!this.amount) return;

    let tokens = Number.parseInt(this.amount.value, 10);
    if (!Number.isFinite(tokens) || tokens < 0) tokens = 0;
    if (tokens > this.spendable) {
      tokens = this.spendable;
      this.amount.value = String(tokens);
    }

    if (!this.worth) return;
    if (tokens === 0) {
      this.worth.textContent = '';
      return;
    }
    // Rounded down to the penny, matching what the discount will actually be.
    const pence = Math.floor((tokens / this.rate) * 100);
    this.worth.textContent = `${tokens} tokens — ${formatMoney(pence)} off.`;
  }

  setStatus(message, isError) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = !message;
    this.status.classList.toggle('gt-spend__status--error', Boolean(isError));
  }

  /** Removes whatever is applied to this basket. */
  releaseHold() {
    if (this.amount) this.amount.value = '0';
    return this.apply();
  }

  async apply() {
    const tokens = Number.parseInt(this.amount?.value ?? '0', 10);
    if (Number.isNaN(tokens)) return;

    this.button.disabled = true;
    this.setStatus(tokens > 0 ? 'Redeeming…' : 'Removing…', false);

    try {
      const cart = await readCart();
      await setHold(cart.token, tokens);

      try {
        await repriceCart(cart.token, tokens);
      } catch (error) {
        // The hold is already placed but the basket won't take the change, so
        // the tokens would sit outside the balance with no discount to show for
        // them. Put them back before reporting the failure.
        await setHold(cart.token, 0).catch(() => {});
        throw error;
      }

      this.setStatus('', false);
    } catch (error) {
      this.setStatus(error.message, true);
    } finally {
      // The element is usually replaced by the re-render; this only matters when
      // it wasn't, such as after an error.
      if (this.button) this.button.disabled = false;
    }
  }
}

// The theme shows a remove "×" beside every cart-level discount, but that button
// removes a *discount code*. Ours is an automatic discount driven by the token
// hold, so the theme's handler can't touch it and the × appears to do nothing.
//
// Intercept it in the capture phase — before the theme's own handler — and
// release the hold instead, which is what the shopper actually meant.
document.addEventListener(
  'click',
  async (event) => {
    const button = event.target.closest?.('.discount__btn-remove');
    if (!button) return;

    const row = button.closest('li');
    if (!row || !/goat tokens/i.test(row.textContent || '')) return;

    event.preventDefault();
    event.stopPropagation();

    const widget = document.querySelector('goat-token-spend');
    if (widget) {
      widget.releaseHold();
      return;
    }

    // No control on this page — release directly, then show the new totals.
    button.disabled = true;
    try {
      const cart = await readCart();
      await setHold(cart.token, 0);
      await repriceCart(cart.token, 0);
    } catch (error) {
      console.warn('[goat-tokens]', error);
      window.location.reload();
    }
  },
  true
);

// Uses the shop's own currency formatting where the theme exposes it, so the
// preview reads the same as every other price on the page.
function formatMoney(pence) {
  const pounds = pence / 100;
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
    }).format(pounds);
  } catch {
    return `£${pounds.toFixed(2)}`;
  }
}

if (!customElements.get('goat-token-spend')) {
  customElements.define('goat-token-spend', GoatTokenSpend);
}
